import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, Space, Modal, Form, Input, DatePicker, InputNumber, message, Tag, Select, Card, Row, Col, Divider, Popconfirm } from 'antd';
import { CheckOutlined, SyncOutlined, SearchOutlined, ClearOutlined, FilterOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { receitaService, matriculaService, alunoService, unidadeService, turmaService, responsavelService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissaoRecurso } from '../hooks/usePermissoes';
import { fetchAllPaginated } from '../helpers/fetchAllPaginated';
import dayjs from 'dayjs';

const ReceitasList = () => {
  const { user } = useAuth();
  const {
    podeCriar,
    podeEditar,
    podeExcluir,
    podeValidar,
    isAdmin,
  } = usePermissaoRecurso('receitas');
  const [receitas, setReceitas] = useState([]);
  const [receitasFiltradas, setReceitasFiltradas] = useState([]);
  const [matriculas, setMatriculas] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [responsavelFinanceiroPorAluno, setResponsavelFinanceiroPorAluno] = useState({});
  const [unidades, setUnidades] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isBaixaModalVisible, setIsBaixaModalVisible] = useState(false);
  const [isManualModalVisible, setIsManualModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedReceita, setSelectedReceita] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [valorParcela, setValorParcela] = useState(0);
  const [form] = Form.useForm();
  const [manualForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [filtros, setFiltros] = useState({
    busca_geral: '',
    status: null,
    mes_vencimento: null,
    forma_pagamento: null,
    responsavel_financeiro: null,
  });

  useEffect(() => {
    fetchReceitas();
    fetchMatriculas();
    fetchAlunos();
    fetchResponsaveisFinanceirosCentrais();
    fetchUnidades();
    fetchTurmas();
  }, []);

  const normalizarId = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const matriculaPorId = useMemo(() => {
    const mapa = new Map();
    matriculas.forEach((m) => {
      const id = normalizarId(m?.id);
      if (id) mapa.set(id, m);
    });
    return mapa;
  }, [matriculas]);

  const alunoPorId = useMemo(() => {
    const mapa = new Map();
    alunos.forEach((a) => {
      const id = normalizarId(a?.id);
      if (id) mapa.set(id, a);
    });
    return mapa;
  }, [alunos]);

  const aplicarFiltros = useCallback(() => {
    let resultado = [...receitas];

    if (filtros.status) {
      resultado = resultado.filter(r => {
        if (filtros.status === 'PAGO') return r.pago === true;
        if (filtros.status === 'PENDENTE') return r.pago === false;
        if (filtros.status === 'VENCIDO') {
          return r.pago === false && dayjs(r.data_vencimento).isBefore(dayjs(), 'day');
        }
        if (filtros.status === 'CANCELADO') return r.status === 'CANCELADO';
        return true;
      });
    }

    if (filtros.mes_vencimento) {
      resultado = resultado.filter(r => {
        if (!r.data_vencimento) return false;
        const mes = dayjs(r.data_vencimento).format('YYYY-MM');
        return mes === filtros.mes_vencimento;
      });
    }

    if (filtros.forma_pagamento) {
      resultado = resultado.filter(r => r.forma_pagamento === filtros.forma_pagamento);
    }

    if (filtros.responsavel_financeiro) {
      resultado = resultado.filter(r => {
        if (r.tipo_receita === 'MANUAL' && !r.id_matricula) return false;
        return getResponsavelFinanceiroNome(r.id_matricula) === filtros.responsavel_financeiro;
      });
    }

    if (filtros.busca_geral) {
      const termo = filtros.busca_geral.toLowerCase();
      resultado = resultado.filter(r => {
        const nomeAluno = getAlunoNome(r.id_matricula).toLowerCase();
        const numeroDoc = r.numero_documento ? r.numero_documento.toLowerCase() : '';
        return nomeAluno.includes(termo) || numeroDoc.includes(termo);
      });
    }

    setReceitasFiltradas(resultado);
  }, [receitas, filtros, matriculas, alunos, responsavelFinanceiroPorAluno]);

  useEffect(() => {
    aplicarFiltros();
  }, [aplicarFiltros]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const limparFiltros = () => {
    setFiltros({
      busca_geral: '',
      status: null,
      mes_vencimento: null,
      forma_pagamento: null,
      responsavel_financeiro: null,
    });
    message.success('Filtros limpos');
  };

  const fetchReceitas = async () => {
    setLoading(true);
    try {
      const limit = 500;
      let skip = 0;
      let todas = [];

      while (true) {
        const response = await receitaService.list({ limit, skip });
        const lote = response.data || [];
        todas = todas.concat(lote);
        if (lote.length < limit) break;
        skip += limit;
      }

      const unicosPorId = new Map();
      todas.forEach((item) => {
        if (item?.id != null) {
          unicosPorId.set(item.id, item);
        }
      });
      setReceitas([...unicosPorId.values()]);
    } catch (error) {
      message.error('Erro ao carregar receitas');
      console.error(error);
    }
    setLoading(false);
  };

  const fetchMatriculas = async () => {
    try {
      const response = await matriculaService.list();
      setMatriculas(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar matrículas');
    }
  };

  const fetchAlunos = async () => {
    try {
      const todosAlunos = await fetchAllPaginated(alunoService.list);
      setAlunos(Array.isArray(todosAlunos) ? todosAlunos : []);
    } catch (error) {
      console.error('Erro ao carregar alunos');
    }
  };

  const fetchResponsaveisFinanceirosCentrais = async () => {
    try {
      const response = await responsavelService.list();
      const responsaveis = Array.isArray(response?.data) ? response.data : [];
      const mapa = {};

      responsaveis.forEach((responsavel) => {
        const alunosVinculados = Array.isArray(responsavel?.alunos) ? responsavel.alunos : [];
        alunosVinculados.forEach((vinculoAluno) => {
          const alunoId = normalizarId(vinculoAluno?.id);
          if (!alunoId) return;

          const pontuacao = vinculoAluno?.is_responsavel_financeiro ? 2 : 1;
          const atual = mapa[alunoId];
          if (atual && atual.pontuacao >= pontuacao) return;

          mapa[alunoId] = {
            pontuacao,
            nome: responsavel?.nome_completo || null,
          };
        });
      });

      setResponsavelFinanceiroPorAluno(mapa);
    } catch (error) {
      console.error('Erro ao carregar responsáveis financeiros centralizados', error);
      setResponsavelFinanceiroPorAluno({});
    }
  };

  const fetchUnidades = async () => {
    try {
      const response = await unidadeService.list();
      setUnidades(response.data);
    } catch (error) {
      console.error('Erro ao carregar unidades');
    }
  };

  const fetchTurmas = async () => {
    try {
      const response = await turmaService.list();
      setTurmas(response.data);
    } catch (error) {
      console.error('Erro ao carregar turmas');
    }
  };

  const handleAddManual = () => {
    manualForm.resetFields();
    setValorParcela(0);
    setIsManualModalVisible(true);
  };

  const calcularValorParcela = () => {
    const valorTotal = manualForm.getFieldValue('valor_total');
    const qtdParcelas = manualForm.getFieldValue('qtd_parcelas');
    
    if (valorTotal && qtdParcelas && qtdParcelas > 0) {
      setValorParcela(valorTotal / qtdParcelas);
    } else {
      setValorParcela(0);
    }
  };

  const handleSubmitManual = async (values) => {
    try {
      const valorTotal = values.valor_total;
      const qtdParcelas = values.qtd_parcelas;
      const valorParcela = valorTotal / qtdParcelas;
      const dataInicio = dayjs(values.data_vencimento);

      // Criar receitas para cada parcela
      for (let i = 1; i <= qtdParcelas; i++) {
        const dataVencimento = dataInicio.add(i - 1, 'month');
        
        const receitaData = {
          tipo_receita: 'MANUAL',
          id_matricula: values.id_matricula || null,
          id_unidade: values.id_unidade,
          parceiro: values.parceiro || null,
          descricao: values.descricao,
          numero_parcela: i,
          valor_total: valorTotal,
          qtd_parcelas: qtdParcelas,
          data_vencimento: dataVencimento.format('YYYY-MM-DD'),
          valor: valorParcela,
          pago: false,
          competencia: dataVencimento.format('YYYY-MM'),
        };

        await receitaService.create(receitaData);
      }

      message.success(`${qtdParcelas} parcela(s) criada(s) com sucesso`);
      setIsManualModalVisible(false);
      manualForm.resetFields();
      fetchReceitas();
    } catch (error) {
      message.error('Erro ao criar receitas');
      console.error(error);
    }
  };

  const handleEdit = (record) => {
    setSelectedReceita(record);
    editForm.setFieldsValue({
      data_vencimento: dayjs(record.data_vencimento),
      valor: record.valor,
      descricao: record.descricao,
    });
    setIsEditModalVisible(true);
  };

  const handleSubmitEdit = async (values) => {
    try {
      const submitData = {
        data_vencimento: values.data_vencimento.format('YYYY-MM-DD'),
        valor: values.valor,
        descricao: values.descricao,
        competencia: values.data_vencimento.format('YYYY-MM'),
      };

      await receitaService.update(selectedReceita.id, submitData);
      message.success('Receita atualizada com sucesso');
      setIsEditModalVisible(false);
      fetchReceitas();
    } catch (error) {
      message.error('Erro ao atualizar receita');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await receitaService.delete(id);
      message.success('Receita excluída com sucesso');
      fetchReceitas();
    } catch (error) {
      message.error('Erro ao excluir receita');
      console.error(error);
    }
  };

  const handleDarBaixa = (record) => {
    setSelectedReceita(record);
    form.setFieldsValue({
      data_pagamento: dayjs(),
      valor_pago: record.valor,
      forma_pagamento: 'PIX',
    });
    setIsBaixaModalVisible(true);
  };

  const handleSubmitBaixa = async (values) => {
    try {
      const submitData = {
        ...values,
        data_pagamento: values.data_pagamento.format('YYYY-MM-DD'),
      };

      await receitaService.darBaixa(selectedReceita.id, submitData);
      message.success('Baixa realizada com sucesso');
      setIsBaixaModalVisible(false);
      fetchReceitas();
    } catch (error) {
      message.error('Erro ao dar baixa na receita');
      console.error(error);
    }
  };

  const handleReverterBaixa = async (receitaId) => {
    try {
      await receitaService.reverterBaixa(receitaId);
      message.success('Baixa revertida com sucesso - status voltou para PENDENTE');
      fetchReceitas();
    } catch (error) {
      message.error('Erro ao reverter baixa da receita');
      console.error(error);
    }
  };

  const getAlunoNome = (matriculaId) => {
    const idMatricula = normalizarId(matriculaId);
    if (!idMatricula) return '-';

    const matricula = matriculaPorId.get(idMatricula);
    if (!matricula) return '-';

    const aluno = alunoPorId.get(normalizarId(matricula.id_alunos));
    return aluno ? aluno.nome_aluno : '-';
  };

  const getMatricula = (matriculaId) => {
    const idMatricula = normalizarId(matriculaId);
    if (!idMatricula) return null;
    return matriculaPorId.get(idMatricula) || null;
  };

  const getUnidadeNome = (unidadeId) => {
    if (!unidadeId) return '-';
    const unidade = unidades.find(u => u.id === unidadeId);
    return unidade ? unidade.nome_fantasia : '-';
  };

  const getTurmaNome = (matriculaId) => {
    const idMatricula = normalizarId(matriculaId);
    if (!idMatricula) return '-';

    const matricula = matriculaPorId.get(idMatricula);
    if (!matricula || !matricula.id_turma) return '-';
    const turma = turmas.find(t => t.id === matricula.id_turma);
    return turma ? turma.nome_turm : '-';
  };

  const getResponsavelFinanceiroNome = (matriculaId) => {
    const idMatricula = normalizarId(matriculaId);
    if (!idMatricula) return '-';

    const matricula = matriculaPorId.get(idMatricula);
    if (!matricula) return '-';

    const alunoId = normalizarId(matricula.id_alunos);
    if (!alunoId) return '-';

    return responsavelFinanceiroPorAluno[alunoId]?.nome || '-';
  };

  const responsaveisFinanceiros = useMemo(() => {
    const nomes = new Set(
      Object.values(responsavelFinanceiroPorAluno)
        .map((item) => item?.nome)
        .filter(Boolean)
    );
    return Array.from(nomes).sort((a, b) => a.localeCompare(b));
  }, [responsavelFinanceiroPorAluno]);

  const getStatusOrdenacao = (record) => {
    if (record.pago) return 2; // Pago
    const hoje = dayjs();
    const vencimento = dayjs(record.data_vencimento);
    if (vencimento.isBefore(hoje, 'day')) return 0; // Vencido
    return 1; // Pendente
  };

  const columns = [
    {
      title: 'Tipo',
      dataIndex: 'tipo_receita',
      key: 'tipo_receita',
      width: 90,
      render: (tipo) => tipo === 'MANUAL' ? <Tag color="blue">Manual</Tag> : <Tag color="green">Matrícula</Tag>,
    },
    {
      title: 'Unidade',
      key: 'unidade',
      width: 150,
      render: (_, record) => {
        if (record.id_unidade) {
          return getUnidadeNome(record.id_unidade);
        }
        return '-';
      },
      sorter: (a, b) => getUnidadeNome(a.id_unidade).localeCompare(getUnidadeNome(b.id_unidade)),
    },
    {
      title: 'Aluno/Parceiro',
      key: 'origem',
      width: 150,
      render: (_, record) => {
        if (record.tipo_receita === 'MANUAL' && !record.id_matricula) {
          return record.parceiro || '-';
        }
        return getAlunoNome(record.id_matricula);
      },
      sorter: (a, b) => {
        const nomeA = (a.tipo_receita === 'MANUAL' && !a.id_matricula) ? (a.parceiro || '-') : getAlunoNome(a.id_matricula);
        const nomeB = (b.tipo_receita === 'MANUAL' && !b.id_matricula) ? (b.parceiro || '-') : getAlunoNome(b.id_matricula);
        return nomeA.localeCompare(nomeB);
      },
    },
    {
      title: 'Resp. Financeiro',
      key: 'responsavel_financeiro',
      width: 180,
      render: (_, record) => {
        if (record.tipo_receita === 'MANUAL' && !record.id_matricula) {
          return '-';
        }
        return getResponsavelFinanceiroNome(record.id_matricula);
      },
      sorter: (a, b) => getResponsavelFinanceiroNome(a.id_matricula).localeCompare(getResponsavelFinanceiroNome(b.id_matricula)),
    },
    {
      title: 'Turma',
      key: 'turma',
      width: 150,
      render: (_, record) => getTurmaNome(record.id_matricula),
      sorter: (a, b) => getTurmaNome(a.id_matricula).localeCompare(getTurmaNome(b.id_matricula)),
    },
    {
      title: 'Parcela',
      dataIndex: 'numero_parcela',
      key: 'numero_parcela',
      width: 80,
      render: (num, record) => record.qtd_parcelas ? `${num}/${record.qtd_parcelas}` : `${num}ª`,
    },
    {
      title: 'Vencimento',
      dataIndex: 'data_vencimento',
      key: 'data_vencimento',
      width: 110,
      render: (date) => dayjs(date).format('DD/MM/YYYY'),
      sorter: (a, b) => dayjs(a.data_vencimento).unix() - dayjs(b.data_vencimento).unix(),
    },
    {
      title: 'Valor Parcela',
      dataIndex: 'valor',
      key: 'valor',
      width: 110,
      render: (valor) => `R$ ${valor?.toFixed(2)}`,
    },
    {
      title: 'Valor Pago',
      dataIndex: 'valor_pago',
      key: 'valor_pago',
      width: 110,
      render: (valor) => valor ? `R$ ${valor?.toFixed(2)}` : '-',
    },
    {
      title: 'Status',
      dataIndex: 'pago',
      key: 'pago',
      width: 90,
      filters: [
        { text: 'Pago', value: true },
        { text: 'Pendente', value: false },
      ],
      onFilter: (value, record) => record.pago === value,
      sorter: (a, b) => getStatusOrdenacao(a) - getStatusOrdenacao(b),
      render: (pago, record) => {
        const hoje = dayjs();
        const vencimento = dayjs(record.data_vencimento);
        let color = 'orange';
        let text = 'Pendente';

        if (pago) {
          color = 'green';
          text = 'Pago';
        } else if (vencimento.isBefore(hoje)) {
          color = 'red';
          text = 'Vencido';
        }

        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Forma Pgto',
      dataIndex: 'forma_pagamento',
      key: 'forma_pagamento',
      width: 100,
      render: (forma) => forma || '-',
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          {!record.pago && podeValidar && (
            <Button
              icon={<CheckOutlined />}
              onClick={() => handleDarBaixa(record)}
              size="small"
              type="primary"
              title="Dar Baixa"
            >
              Baixa
            </Button>
          )}
          {record.pago && podeValidar && isAdmin && (
            <Popconfirm
              title="Tem certeza que deseja reverter esta baixa? O status voltará para PENDENTE."
              onConfirm={() => handleReverterBaixa(record.id)}
              okText="Sim"
              cancelText="Não"
            >
              <Button
                icon={<SyncOutlined />}
                size="small"
                type="default"
                title="Reverter Baixa"
              >
                Reverter
              </Button>
            </Popconfirm>
          )}
          {(podeEditar || podeExcluir) && (
            <>
              {podeEditar && (
                <Button
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                  size="small"
                  title="Editar"
                />
              )}
              {podeExcluir && (
                <Popconfirm
                  title="Tem certeza que deseja excluir esta receita?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Sim"
                  cancelText="Não"
                >
                  <Button
                    icon={<DeleteOutlined />}
                    size="small"
                    danger
                    title="Excluir"
                  />
                </Popconfirm>
              )}
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Painel de Filtros */}
      <Card 
        title={
          <Space>
            <FilterOutlined />
            Filtros de Busca
            <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#888' }}>
              ({receitasFiltradas.length} de {receitas.length} receitas)
            </span>
          </Space>
        }
        extra={
          <Button type="text" size="small" onClick={() => setMostrarFiltros(!mostrarFiltros)}>
            {mostrarFiltros ? 'Ocultar' : 'Mostrar'}
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        {mostrarFiltros && (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Busca Geral</div>
              <Input
                placeholder="Pesquisar por aluno, documento..."
                prefix={<SearchOutlined />}
                value={filtros.busca_geral}
                onChange={(e) => handleFiltroChange('busca_geral', e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Status</div>
              <Select
                placeholder="Todos os status"
                value={filtros.status}
                onChange={(value) => handleFiltroChange('status', value)}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="PENDENTE">Pendente</Select.Option>
                <Select.Option value="PAGO">Pago</Select.Option>
                <Select.Option value="VENCIDO">Vencido</Select.Option>
                <Select.Option value="CANCELADO">Cancelado</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Mês de Vencimento</div>
              <DatePicker
                placeholder="Selecione o mês"
                picker="month"
                value={filtros.mes_vencimento ? dayjs(filtros.mes_vencimento) : null}
                onChange={(date) => handleFiltroChange('mes_vencimento', date ? date.format('YYYY-MM') : null)}
                style={{ width: '100%' }}
                format="MM/YYYY"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Forma de Pagamento</div>
              <Select
                placeholder="Todas as formas"
                value={filtros.forma_pagamento}
                onChange={(value) => handleFiltroChange('forma_pagamento', value)}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="DINHEIRO">Dinheiro</Select.Option>
                <Select.Option value="PIX">PIX</Select.Option>
                <Select.Option value="CARTAO_CREDITO">Cartão Crédito</Select.Option>
                <Select.Option value="CARTAO_DEBITO">Cartão Débito</Select.Option>
                <Select.Option value="BOLETO">Boleto</Select.Option>
                <Select.Option value="TRANSFERENCIA">Transferência</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Resp. Financeiro</div>
              <Select
                placeholder="Todos os responsáveis"
                value={filtros.responsavel_financeiro}
                onChange={(value) => handleFiltroChange('responsavel_financeiro', value)}
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
              >
                {responsaveisFinanceiros.map((nome) => (
                  <Select.Option key={nome} value={nome}>
                    {nome}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>&nbsp;</div>
              <Button icon={<ClearOutlined />} onClick={limparFiltros} block>
                Limpar Filtros
              </Button>
            </Col>
          </Row>
        )}
      </Card>

      <div style={{ marginBottom: 16 }}>
        <Space>
          {podeCriar && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleAddManual}
            >
              Adicionar Receita Manual
            </Button>
          )}
          <Button
            icon={<SyncOutlined />}
            onClick={fetchReceitas}
          >
            Atualizar
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={receitasFiltradas}
        loading={loading}
        rowKey="id"
        pagination={{ 
          pageSize: 15,
          showSizeChanger: true,
          showTotal: (total) => `Total de ${total} receitas`,
          pageSizeOptions: ['15', '30', '50', '100']
        }}
        scroll={{ x: 1200, y: 520 }}
        sticky
      />

      <Modal
        title="Dar Baixa na Receita"
        open={isBaixaModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsBaixaModalVisible(false)}
        width={600}
      >
        {selectedReceita && (
          <div style={{ marginBottom: 16, padding: 12, background: '#f0f2f5', borderRadius: 4 }}>
            <p><strong>Aluno:</strong> {getAlunoNome(selectedReceita.id_matricula)}</p>
            <p><strong>Parcela:</strong> {selectedReceita.numero_parcela}ª</p>
            <p><strong>Vencimento:</strong> {dayjs(selectedReceita.data_vencimento).format('DD/MM/YYYY')}</p>
            <p><strong>Valor:</strong> R$ {selectedReceita.valor?.toFixed(2)}</p>
            {selectedReceita.tipo_receita !== 'MANUAL' && (
              <>
                <p><strong>Valor Total Matrícula:</strong> R$ {getMatricula(selectedReceita.id_matricula)?.valor_total?.toFixed(2) || '-'}</p>
                <p><strong>Valor Parcela (sem desconto):</strong> R$ {getMatricula(selectedReceita.id_matricula)?.valor_parcela?.toFixed(2) || '-'}</p>
              </>
            )}
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitBaixa}
        >
          <Form.Item
            name="data_pagamento"
            label="Data de Pagamento"
            rules={[{ required: true, message: 'Data é obrigatória' }]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="valor_pago"
            label="Valor Pago (R$)"
            rules={[{ required: true, message: 'Valor é obrigatório' }]}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              placeholder="0.00"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="forma_pagamento"
            label="Forma de Pagamento"
            rules={[{ required: true, message: 'Forma de pagamento é obrigatória' }]}
          >
            <Select placeholder="Selecione">
              <Select.Option value="DINHEIRO">Dinheiro</Select.Option>
              <Select.Option value="CARTAO_CREDITO">Cartão de Crédito</Select.Option>
              <Select.Option value="CARTAO_DEBITO">Cartão de Débito</Select.Option>
              <Select.Option value="BOLETO">Boleto</Select.Option>
              <Select.Option value="PIX">PIX</Select.Option>
              <Select.Option value="TRANSFERENCIA">Transferência</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="observacoes" label="Observações">
            <Input.TextArea rows={3} placeholder="Observações sobre o pagamento" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Adicionar Receita Manual"
        open={isManualModalVisible}
        onOk={() => manualForm.submit()}
        onCancel={() => setIsManualModalVisible(false)}
        width={600}
      >
        <Form
          form={manualForm}
          layout="vertical"
          onFinish={handleSubmitManual}
        >
          <Form.Item
            name="id_matricula"
            label="Vincular à Matrícula (Opcional)"
          >
            <Select
              placeholder="Selecione uma matrícula (opcional)"
              showSearch
              allowClear
              optionFilterProp="children"
            >
              {matriculas.map((mat) => {
                const aluno = alunos.find(a => a.id === mat.id_alunos);
                return (
                  <Select.Option key={mat.id} value={mat.id}>
                    {`Mat. ${mat.id} - ${aluno?.nome_aluno || 'Aluno'}`}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="id_unidade"
            label="Unidade"
            rules={[{ required: true, message: 'Selecione uma unidade' }]}
          >
            <Select
              placeholder="Selecione a unidade"
              showSearch
              optionFilterProp="children"
            >
              {unidades.map(u => (
                <Select.Option key={u.id} value={u.id}>
                  {u.nome_fantasia}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="parceiro"
            label="Parceiro (Opcional)"
          >
            <Input placeholder="Nome do parceiro ou empresa" />
          </Form.Item>

          <Divider style={{ margin: '12px 0' }} />

          <Form.Item
            name="descricao"
            label="Descrição"
            rules={[{ required: true, message: 'Digite uma descrição' }]}
          >
            <Input.TextArea rows={2} placeholder="Descrição da receita manual" />
          </Form.Item>

          <Form.Item
            name="valor_total"
            label="Valor Total (R$)"
            rules={[{ required: true, message: 'Valor é obrigatório' }]}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              placeholder="0.00"
              style={{ width: '100%' }}
              onChange={(value) => {
                const qtd = manualForm.getFieldValue('qtd_parcelas') || 1;
                calcularValorParcela(value, qtd);
              }}
            />
          </Form.Item>

          <Form.Item
            name="qtd_parcelas"
            label="Quantidade de Parcelas"
            rules={[{ required: true, message: 'Quantidade é obrigatória' }]}
            initialValue={1}
          >
            <InputNumber
              min={1}
              max={12}
              style={{ width: '100%' }}
              onChange={(value) => {
                const total = manualForm.getFieldValue('valor_total') || 0;
                calcularValorParcela(total, value);
              }}
            />
          </Form.Item>

          <Form.Item
            name="data_vencimento"
            label="Data do Primeiro Vencimento"
            rules={[{ required: true, message: 'Data é obrigatória' }]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>

          {valorParcela > 0 && (
            <div style={{ 
              padding: '12px', 
              background: '#e6f7ff', 
              border: '1px solid #91d5ff',
              borderRadius: '4px',
              marginTop: '12px'
            }}>
              <strong>Valor por Parcela:</strong> R$ {valorParcela.toFixed(2)}
            </div>
          )}
        </Form>
      </Modal>

      <Modal
        title="Editar Receita"
        open={isEditModalVisible}
        onOk={() => editForm.submit()}
        onCancel={() => setIsEditModalVisible(false)}
        width={500}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleSubmitEdit}
        >
          <Form.Item
            name="descricao"
            label="Descrição"
          >
            <Input.TextArea rows={2} placeholder="Descrição da receita" />
          </Form.Item>

          <Form.Item
            name="data_vencimento"
            label="Data de Vencimento"
            rules={[{ required: true, message: 'Data é obrigatória' }]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="valor"
            label="Valor (R$)"
            rules={[{ required: true, message: 'Valor é obrigatório' }]}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              placeholder="0.00"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReceitasList;
